import { LimeCheckboxGroup } from "@/Components/NextBase/LimeCheckboxGroup";
import { t } from "@lingui/core/macro";
import { api } from "@/lib/api-client";
import { useAppointmentModalStore } from "../../useAppointmentModalStore_";
import { GetCustomerSubscriptions } from "@/server-types";
import { useEffect } from "react";
import { CustomerSubscriptionItem } from "./CustomerSubscriptionItem";
import { Divider } from "@heroui/react";

export const CustomerSubscriptionsList = ({
  customerId,
  customerSubscriptionId,
  isNewAppointment,
  isPreScheduled,
  onCustomerSubscriptionChange,
}: {
  customerId: number;
  customerSubscriptionId: number | null;
  isNewAppointment: boolean;
  isPreScheduled: boolean;
  onCustomerSubscriptionChange: (customerSubscriptionId: number | null) => void;
}) => {
  const { data: customerSubscriptionsResponse } =
    api.customer.useGetCustomerSubscriptions({
      customerId: customerId ?? -1,
      status: "active",
    });

  const customerSubscriptions = customerSubscriptionsResponse?.customerSubscriptionsWithUsage ?? [];

  const { selectedServices } = useAppointmentModalStore((state) => ({
    selectedServices: state.selectedServices,
  }));

  const selectedServiceIds = selectedServices.map(
    (service: { serviceId: number }) => service.serviceId,
  );

  const firstAvailableSubscription = customerSubscriptions?.find(
    (subscription) =>
      areServicesIncludedInSubscription(subscription, selectedServiceIds),
  );

  useEffect(() => {
    const shouldSetSubscription =
      firstAvailableSubscription &&
      (isNewAppointment || customerSubscriptionId);

    if (!shouldSetSubscription) {
      return;
    }

    onCustomerSubscriptionChange(firstAvailableSubscription.id);
  }, [selectedServices, customerSubscriptions]);

  const selectedCustomerSubscriptionId = (() => {
    if (!isNewAppointment) {
      return customerSubscriptionId ? [customerSubscriptionId.toString()] : [];
    }

    if (!customerSubscriptionId) {
      return [];
    }

    const customerSubscription = customerSubscriptions.find(
      (sub) => sub.id === customerSubscriptionId,
    );

    if (!customerSubscription) {
      return [];
    }

    return [customerSubscription.id.toString()];
  })();

  const subscriptions = (() => {
    if (!isNewAppointment && customerSubscriptionId) {
      const subscription = customerSubscriptions.find(
        (sub) => sub.id === customerSubscriptionId,
      );

      if (!subscription) {
        return [];
      }

      return [subscription];
    }

    return customerSubscriptions;
  })();

  return (
    <>
      <Divider />
      <div className="flex flex-col gap-1">
        <p className="text-sm font-medium">
          {isNewAppointment ? t`Izberi naročnino` : t`Izbrana naročnina`}
        </p>
        <LimeCheckboxGroup
          className="flex w-full flex-col gap-2"
          value={selectedCustomerSubscriptionId}
          onValueChange={(values) => {
            onCustomerSubscriptionChange(
              values.length > 0 ? parseInt(values.pop()!) : null,
            );
          }}
        >
          {subscriptions.map((customerSubscription) => {
            const isAvailable = areServicesIncludedInSubscription(
              customerSubscription,
              selectedServiceIds,
            );
            return (
              <CustomerSubscriptionItem
                key={customerSubscription.id}
                value={customerSubscription.id.toString()}
                isDisabled={!isAvailable}
                customerSubscription={customerSubscription}
                isAvailable={isAvailable}
              />
            );
          })}
        </LimeCheckboxGroup>
      </div>
      <Divider />
    </>
  );
};

const areServicesIncludedInSubscription = (
  customerSubscription: GetCustomerSubscriptions["response"]["customerSubscriptionsWithUsage"][number],
  selectedServiceIds: number[],
) => {
  if (!customerSubscription.customerSubscriptionServices) {
    return false;
  }

  const subscriptionServiceIds =
    customerSubscription.customerSubscriptionServices.map(
      (service: { serviceId: number }) => service.serviceId,
    );

  return selectedServiceIds.every((serviceId) =>
    subscriptionServiceIds.includes(serviceId),
  );
};
